import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  Avatar,
  Rating,
  Divider,
  Skeleton,
  Alert,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Verified as VerifiedIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Phone as PhoneIcon,
  VideoCall as VideoCallIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import HotjarScript from '../components/HotjarScript';

import MarketplaceHero from '../components/Marketplace/MarketplaceHero';
import SEOMetaTags from '../components/SEO/SEOMetaTags';
import { fetchDoctors } from '../features/doctors/doctorsSlice';
import {
  selectDoctors,
  selectDoctorsLoading,
  selectDoctorsError,
  selectDoctorsCount,
} from '../features/doctors/doctorsSlice';
import DoctorCard from '../components/HomePage/DoctorCard';

/**
 * Marketplace Homepage - Main landing page for finding mental health professionals
 * SEO optimized, marketplace-focused experience
 */
const MarketplaceHomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux state
  const doctors = useSelector(selectDoctors);
  const loading = useSelector(selectDoctorsLoading);
  const error = useSelector(selectDoctorsError);
  const totalCount = useSelector(selectDoctorsCount);

  // Local state
  const [featuredDoctors, setFeaturedDoctors] = useState([]);
  const [searchParams, setSearchParams] = useState({});

  // Fetch featured doctors on mount
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const result = await dispatch(
          fetchDoctors({
            page: 1,
            page_size: 6,
            sortBy: 'rating',
          })
        ).unwrap();
        
        if (result?.results) {
          // Show verified doctors first
          const verified = result.results.filter(d => d.is_verified);
          const others = result.results.filter(d => !d.is_verified);
          setFeaturedDoctors([...verified, ...others].slice(0, 6));
        }
      } catch (err) {
        console.error('Error fetching featured doctors:', err);
      }
    };

    fetchFeatured();
  }, [dispatch]);

  // Handle search from hero
  const handleSearch = useCallback((params) => {
    setSearchParams(params);
    const queryString = new URLSearchParams({
      ...(params.query && { search: params.query }),
      ...(params.location && { city: params.location }),
    }).toString();
    navigate(`/doctors?${queryString}`);
  }, [navigate]);

  // Popular searches
  const popularSearches = [
    'Psychiatrist',
    'Psychologist',
    'Therapist',
    'Depression',
    'Anxiety',
    'Online Therapy',
    'Couples Counseling',
    'Child Psychology',
  ];

  // Comprehensive SEO structured data for maximum SEO and LLM compatibility
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://app.PsychePoint.com';
  
  // Main WebSite schema
  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PsychePoint - Find Verified Mental Health Professionals',
    alternateName: 'PsychePoint',
    description: 'Find verified mental health professionals - psychiatrists, psychologists, therapists, and counselors. Book appointments online. Get professional mental health care from trusted professionals in India and worldwide.',
    url: siteUrl,
    inLanguage: ['en', 'hi'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/doctors?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    publisher: {
      '@type': 'Organization',
      name: 'PsychePoint',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
      sameAs: [
        `${siteUrl}`,
        // Add social media URLs if available
      ],
    },
  };

  // Organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PsychePoint',
    description: 'Leading platform for connecting patients with verified mental health professionals including psychiatrists, psychologists, therapists, and counselors across India and worldwide.',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'Hindi'],
    },
    areaServed: {
      '@type': 'Country',
      name: ['India', 'International'],
    },
    serviceType: 'Mental Health Services Marketplace',
  };

  // LocalBusiness schema (if applicable)
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'PsychePoint - Mental Health Professionals Marketplace',
    description: 'Online marketplace connecting patients with verified mental health professionals for consultations, therapy, and mental health support.',
    url: siteUrl,
    priceRange: '$$',
    acceptsReservations: 'Yes',
    currenciesAccepted: 'INR, USD',
  };

  // FAQ Schema for common questions
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I find a mental health professional near me?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Use our search tool to find verified psychiatrists, psychologists, and therapists by location, specialization, or name. Filter by consultation type (online, in-person), availability, and ratings.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I book appointments online?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you can book appointments instantly online. Choose from video consultations, phone consultations, or in-person visits based on the professional\'s availability.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are all professionals verified?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, all mental health professionals on PsychePoint are verified with proper credentials, licenses, and qualifications. We verify MCI/RCI registration numbers and professional credentials.',
        },
      },
      {
        '@type': 'Question',
        name: 'What types of mental health professionals can I find?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can find psychiatrists, clinical psychologists, counseling psychologists, psychotherapists, and counselors specializing in various areas including depression, anxiety, trauma, couples therapy, and more.',
        },
      },
    ],
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Find Mental Health Professionals',
        item: `${siteUrl}/doctors`,
      },
    ],
  };

  // ItemList schema for featured doctors (LLM-friendly)
  const doctorsListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Featured Mental Health Professionals',
    description: 'Verified psychiatrists, psychologists, and therapists available for consultation',
    numberOfItems: featuredDoctors.length,
    itemListElement: featuredDoctors.slice(0, 6).map((doctor, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Physician',
        '@id': `${siteUrl}/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`,
        name: `Dr. ${doctor.name || doctor.full_name || 'Mental Health Professional'}`,
        jobTitle: doctor.specialities?.[0]?.name || 'Mental Health Professional',
        medicalSpecialty: doctor.specialities?.map(s => s.name) || [],
        image: doctor.profile_image || doctor.photo || `${siteUrl}/images/default-doctor.jpg`,
        url: `${siteUrl}/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`,
        ...(doctor.consultation_fee && {
          offers: {
            '@type': 'Offer',
            price: doctor.consultation_fee,
            priceCurrency: 'INR',
          },
        }),
      },
    })),
  };

  // Combine all structured data
  const allStructuredData = [
    webSiteSchema,
    organizationSchema,
    localBusinessSchema,
    faqSchema,
    breadcrumbSchema,
    ...(featuredDoctors.length > 0 ? [doctorsListSchema] : []),
  ];

  return (
    <>
      {/* Hotjar Tracking */}
      <HotjarScript siteId={5252549} version={6} />
      
      <SEOMetaTags
        title="Find Verified Mental Health Professionals - Psychiatrists, Psychologists, Therapists | PsychePoint"
        description="Book appointments with verified psychiatrists, psychologists, therapists, and counselors. Online and in-person consultations available across India and worldwide. Find the right mental health professional for depression, anxiety, trauma, and more. Verified credentials, real reviews, instant booking."
        keywords={[
          'mental health professional', 'psychiatrist', 'psychologist', 'therapist', 'counselor',
          'online therapy', 'appointment booking', 'mental health services', 'depression therapist',
          'anxiety counselor', 'psychiatrist near me', 'online psychiatrist', 'video consultation',
          'mental health care', 'therapy online', 'psychiatric consultation', 'mental wellness'
        ]}
        structuredData={allStructuredData}
        url="/"
      />
      
      {/* Additional Semantic HTML for SEO and LLM */}
      <Helmet>
        {/* Additional meta tags for better SEO */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta name="application-name" content="PsychePoint" />
        <meta name="apple-mobile-web-app-title" content="PsychePoint" />
        
        {/* Enhanced Open Graph for social sharing */}
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="hi_IN" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero Section */}
        <MarketplaceHero onSearch={handleSearch} popularSearches={popularSearches} />

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          {/* Why Choose PsychePoint - Enhanced with semantic HTML */}
          <Box component="section" aria-labelledby="why-choose-mindwell" sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h2" component="h2" id="why-choose-mindwell" fontWeight={700} gutterBottom>
              Why Choose PsychePoint?
            </Typography>
            <Typography variant="body1" component="p" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 4 }}>
              We connect you with verified, experienced mental health professionals who are committed to your wellbeing. 
              Our platform features only credentialed psychiatrists, psychologists, therapists, and counselors verified 
              through medical licensing boards and professional associations.
            </Typography>
            <Grid container spacing={3}>
              {[
                {
                  icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
                  title: 'Verified Professionals',
                  description: 'All doctors are verified with proper credentials and licenses',
                },
                {
                  icon: <TimeIcon sx={{ fontSize: 40 }} />,
                  title: 'Easy Booking',
                  description: 'Book appointments in minutes, online or in-person',
                },
                {
                  icon: <StarIcon sx={{ fontSize: 40 }} />,
                  title: 'Trusted Reviews',
                  description: 'Read real reviews from patients who have visited',
                },
                {
                  icon: <PhoneIcon sx={{ fontSize: 40 }} />,
                  title: 'Multiple Formats',
                  description: 'Choose from video, phone, or in-person consultations',
                },
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        height: '100%',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-4px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Featured Doctors - Enhanced with semantic HTML */}
          <Box component="section" aria-labelledby="featured-professionals" sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography variant="h2" component="h2" id="featured-professionals" fontWeight={700} gutterBottom>
                  Featured Mental Health Professionals
                </Typography>
                <Typography variant="body1" component="p" color="text.secondary">
                  Browse our verified psychiatrists, psychologists, therapists, and counselors. All professionals 
                  are credentialed and verified with proper licenses and qualifications.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/doctors')}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Grid container spacing={3}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card>
                      <Skeleton variant="rectangular" height={200} />
                      <CardContent>
                        <Skeleton variant="text" height={32} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                Failed to load doctors. Please try again later.
              </Alert>
            ) : featuredDoctors.length > 0 ? (
              <Grid container spacing={3} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {featuredDoctors.map((doctor, index) => (
                  <Grid item xs={12} sm={6} md={4} key={doctor.id} component="li">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <article itemScope itemType="https://schema.org/Physician">
                        <DoctorCard 
                          doctor={doctor}
                          onSelect={(doctor) => navigate(`/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`)}
                          onBookAppointment={(doctor) => navigate(`/doctor/${doctor.id || doctor.uuid || doctor.doctor_id}`)}
                        />
                      </article>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No doctors available at the moment. Please check back later.
              </Alert>
            )}

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/doctors')}
              >
                Browse All Professionals
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* How It Works - Enhanced with semantic HTML */}
          <Box component="section" aria-labelledby="how-it-works" sx={{ mb: 6 }}>
            <Typography variant="h2" component="h2" id="how-it-works" fontWeight={700} textAlign="center" gutterBottom>
              How It Works
            </Typography>
            <Typography variant="body1" component="p" color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: '700px', mx: 'auto' }}>
              Find and book appointments with mental health professionals in four simple steps. 
              Our platform makes it easy to connect with the right professional for your needs.
            </Typography>
            <Grid container spacing={4} component="ol" sx={{ mt: 2, listStyle: 'none', p: 0, m: 0 }}>
              {[
                {
                  step: '1',
                  title: 'Search & Find',
                  description: 'Search for mental health professionals by specialization, location, or name',
                },
                {
                  step: '2',
                  title: 'Review Profiles',
                  description: 'Read detailed profiles, qualifications, reviews, and ratings',
                },
                {
                  step: '3',
                  title: 'Book Appointment',
                  description: 'Choose a convenient date and time, book instantly',
                },
                {
                  step: '4',
                  title: 'Get Help',
                  description: 'Attend your consultation online or in-person and get the support you need',
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                      }}
                    >
                      {item.step}
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default MarketplaceHomePage;

